import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Clock, Upload, FileCheck, XCircle, Home, RefreshCw } from 'lucide-react';
import axios from 'axios';

const PaymentStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = location.state || {};
  
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) {
      navigate('/');
      return;
    }
    fetchPaymentStatus();
    
    // Poll every 10 seconds for status updates
    const interval = setInterval(fetchPaymentStatus, 10000);
    return () => clearInterval(interval);
  }, [orderId]);

  const fetchPaymentStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/bank-transfer/${orderId}`
      );
      setPayment(response.data.payment);
      
      // Navigate to success/rejection pages based on status
      if (response.data.payment.status === 'verified') {
        navigate('/payment/success', { state: { payment: response.data.payment } });
      } else if (response.data.payment.status === 'rejected') {
        navigate('/payment/rejected', { state: { payment: response.data.payment } });
      }
    } catch (error) {
      console.error('Error fetching payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      awaiting_payment: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Awaiting Payment' },
      proof_uploaded: { color: 'bg-blue-100 text-blue-800', icon: Upload, text: 'Under Review' },
      under_review: { color: 'bg-blue-100 text-blue-800', icon: FileCheck, text: 'Under Review' },
      verified: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Verified' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejected' },
      expired: { color: 'bg-gray-100 text-gray-800', icon: Clock, text: 'Expired' }
    };
    return statusMap[status] || statusMap.awaiting_payment;
  };

  const timeline = [
    { step: 'Order Created', status: 'completed', date: payment?.created_at },
    { step: 'Payment Proof Uploaded', status: payment?.proof_file_path ? 'completed' : 'pending', date: payment?.updated_at },
    { step: 'Under Review', status: payment?.status === 'proof_uploaded' || payment?.status === 'under_review' ? 'active' : payment?.verified_at ? 'completed' : 'pending' },
    { step: 'Payment Verified', status: payment?.status === 'verified' ? 'completed' : 'pending', date: payment?.verified_at },
    { step: 'Service Activated', status: payment?.status === 'verified' ? 'completed' : 'pending' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading payment status...</p>
        </div>
      </div>
    );
  }

  const StatusIcon = getStatusInfo(payment?.status).icon;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Success Header */}
        <Card className="mb-6 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="p-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Proof Submitted!</h1>
            <p className="text-gray-600 mb-4">
              Thank you! Your payment proof has been submitted successfully.
            </p>
            <div className="flex items-center justify-center gap-4 text-sm">
              <div>
                <span className="text-gray-600">Order ID: </span>
                <span className="font-bold text-blue-600">{payment?.order_id}</span>
              </div>
              <div>
                <span className="text-gray-600">Submitted: </span>
                <span className="font-semibold">{new Date(payment?.updated_at).toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Timeline */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Payment Status</CardTitle>
                  <Badge className={getStatusInfo(payment?.status).color}>
                    <StatusIcon className="h-4 w-4 mr-1" />
                    {getStatusInfo(payment?.status).text}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {timeline.map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          item.status === 'completed' ? 'bg-green-600' :
                          item.status === 'active' ? 'bg-blue-600' :
                          'bg-gray-300'
                        }`}>
                          {item.status === 'completed' ? (
                            <CheckCircle className="h-5 w-5 text-white" />
                          ) : item.status === 'active' ? (
                            <Clock className="h-5 w-5 text-white animate-pulse" />
                          ) : (
                            <div className="w-3 h-3 rounded-full bg-white" />
                          )}
                        </div>
                        {index < timeline.length - 1 && (
                          <div className={`w-0.5 h-16 ${
                            item.status === 'completed' ? 'bg-green-600' : 'bg-gray-300'
                          }`} />
                        )}
                      </div>
                      <div className="flex-1 pb-8">
                        <p className={`font-semibold ${
                          item.status === 'completed' ? 'text-green-600' :
                          item.status === 'active' ? 'text-blue-600' :
                          'text-gray-500'
                        }`}>
                          {item.step}
                        </p>
                        {item.date && (
                          <p className="text-sm text-gray-500 mt-1">
                            {new Date(item.date).toLocaleString()}
                          </p>
                        )}
                        {item.status === 'active' && (
                          <p className="text-sm text-blue-600 mt-1">In Progress...</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Uploaded Proof */}
            {payment?.proof_file_path && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Uploaded Proof</CardTitle>
                </CardHeader>
                <CardContent>
                  {payment.proof_file_path.endsWith('.pdf') ? (
                    <div className="bg-gray-100 p-4 rounded text-center">
                      <FileCheck className="h-12 w-12 text-gray-600 mx-auto mb-2" />
                      <p className="text-sm font-medium">PDF Document</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${payment.proof_file_path}`, '_blank')}
                      >
                        View
                      </Button>
                    </div>
                  ) : (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${payment.proof_file_path}`}
                      alt="Payment proof"
                      className="w-full rounded border"
                    />
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full mt-2"
                    onClick={() => navigate('/payment/upload-proof', { state: { orderId: payment.order_id, amount: payment.amount } })}
                  >
                    Re-upload
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* What's Next */}
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-base">What's Next?</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-blue-900 space-y-2">
                  <li>• Our team is reviewing your payment</li>
                  <li>• Verification usually takes 2-24 hours</li>
                  <li>• You'll receive email notification</li>
                  <li>• Check status anytime in "My Orders"</li>
                </ul>
              </CardContent>
            </Card>

            {/* Help */}
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-2">Need help?</p>
                <p className="text-sm">
                  📧 <a href="mailto:payments@zeilian.co" className="text-blue-600 underline">payments@zeilian.co</a>
                </p>
                <p className="text-sm">
                  📞 <a href="tel:+94112345678" className="text-blue-600 underline">+94 11 234 5678</a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex gap-4">
          <Button
            onClick={() => navigate('/dashboard')}
            className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Home className="h-4 w-4 mr-2" />
            Go to Dashboard
          </Button>
          <Button
            variant="outline"
            onClick={fetchPaymentStatus}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Status
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;